<template>
  <div class="calculator">
    <!-- 工具栏区域 -->
    <div class="toolbar-area">
      <div class="toolbar-left">
        <button
          class="btn btn-menu"
          @click="toggleDrawer"
          title="切换计算器模式"
        >
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path
              d="M2 3h12v1H2V3zm0 4h12v1H2V7zm0 4h12v1H2v-1z"
              fill="currentColor"
            />
          </svg>
        </button>
        <div class="mode-display">
          {{ currentModeText }}
        </div>
      </div>
      <div class="toolbar-right">
        <button class="btn btn-memory" @click="toggleHistory" title="历史记录">
          <svg viewBox="0 0 1024 1024" width="22" height="22">
            <path
              d="M537.6 95.6928A409.2928 409.2928 0 0 0 159.3344 351.232l-23.2448-43.4176a28.16 28.16 0 0 0-49.664 26.5728l55.1936 102.9632a28.2112 28.2112 0 0 0 24.832 14.848 27.6992 27.6992 0 0 0 12.4416-2.9184l109.9776-54.272a28.16 28.16 0 0 0-24.9344-50.5344l-51.2 25.6a351.6416 351.6416 0 1 1 12.4416 294.4512 28.16 28.16 0 0 0-50.0736 25.6A407.9616 407.9616 0 1 0 537.6 95.6928z"
              fill="#555555"
            ></path>
            <path
              d="M522.9056 297.7792a28.16 28.16 0 0 0-28.16 28.16V568.32a28.16 28.16 0 0 0 28.16 28.16h205.8752a28.16 28.16 0 0 0 0-56.32h-177.7152V325.9392a28.16 28.16 0 0 0-28.16-28.16z"
              fill="#555555"
            ></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- 显示区域 -->
    <div class="display-area">
      <!-- 表达式显示 -->
      <div class="expression">
        {{ expressionText }}
      </div>

      <!-- 主显示屏 -->
      <div
        ref="displayRef"
        class="display"
        :style="{ fontSize: dynamicFontSize }"
      >
        {{ displayText }}
      </div>
    </div>

    <!-- 内存按钮区域 -->
    <div class="memory-section">
      <button
        class="btn btn-memory"
        @click="memoryClear"
        :disabled="memoryList.length === 0"
        title="清除所有记忆"
      >
        MC
      </button>
      <button
        class="btn btn-memory"
        @click="memoryRecall"
        :disabled="memoryList.length === 0"
        title="记忆调用"
      >
        MR
      </button>
      <button class="btn btn-memory" @click="memoryAdd" title="记忆加法">
        M+
      </button>
      <button class="btn btn-memory" @click="memorySubtract" title="记忆减法">
        M-
      </button>
      <button class="btn btn-memory" @click="memoryStore" title="记忆存储">
        MS
      </button>
      <button
        class="btn btn-memory"
        @click="toggleMemoryList"
        :disabled="memoryList.length === 0"
        title="内存记录"
      >
        M∨
      </button>
    </div>

    <!-- 按钮区域 -->
    <div class="buttons-area">
      <!-- 科学计算器额外功能行 -->
      <div
        v-if="currentMode === 'scientific'"
        class="button-row scientific-row"
      >
        <button
          class="btn btn-function"
          @click="scientificFunction('sin')"
          title="正弦"
        >
          sin
        </button>
        <button
          class="btn btn-function"
          @click="scientificFunction('cos')"
          title="余弦"
        >
          cos
        </button>
        <button
          class="btn btn-function"
          @click="scientificFunction('tan')"
          title="正切"
        >
          tan
        </button>
        <button
          class="btn btn-function"
          @click="scientificFunction('log')"
          title="常用对数"
        >
          log
        </button>
      </div>

      <div
        v-if="currentMode === 'scientific'"
        class="button-row scientific-row"
      >
        <button
          class="btn btn-function"
          @click="scientificFunction('ln')"
          title="自然对数"
        >
          ln
        </button>
        <button
          class="btn btn-function"
          @click="scientificFunction('exp')"
          title="e的x次方"
        >
          e^x
        </button>
        <button
          class="btn btn-function"
          @click="scientificFunction('pow')"
          title="x的y次方"
        >
          x^y
        </button>
        <button class="btn btn-function" @click="factorial" title="阶乘">
          n!
        </button>
      </div>

      <!-- 第一行：百分号、CE、C、退格 -->
      <div class="button-row">
        <button class="btn btn-function" @click="percentage">%</button>
        <button class="btn btn-clear" @click="clearEntry">CE</button>
        <button class="btn btn-clear" @click="clearAll">C</button>
        <button class="btn btn-function" @click="backspace">
          <svg viewBox="0 0 1395 1024" width="20" height="20">
            <path
              d="M1229.851676 1024h-719.567568a23.911784 23.911784 0 0 1-3.90227-1.051676 96.864865 96.864865 0 0 1-64.152216-28.118486L28.118486 580.718703a97.639784 97.639784 0 0 1 0-138.046271L442.229622 28.588973A97.224649 97.224649 0 0 1 509.868973 0h719.982703a166.054054 166.054054 0 0 1 166.054054 166.054054v691.891892a166.054054 166.054054 0 0 1-166.054054 166.054054z m110.702702-820.556108A149.808432 149.808432 0 0 0 1189.113081 55.351351H532.48a89.558486 89.558486 0 0 0-61.689081 25.378595L92.132324 454.739027a78.01773 78.01773 0 0 0 0 114.604973l378.658595 373.178811a89.254054 89.254054 0 0 0 58.506378 25.074162 23.081514 23.081514 0 0 0 3.542487 0.940973h656.273297a149.808432 149.808432 0 0 0 151.441297-148.009514v-617.167567z m-253.536864 519.776865l-19.566703 19.566702a13.837838 13.837838 0 0 1-19.566703 0l-205.491892-205.491891-205.491892 205.491891a13.837838 13.837838 0 0 1-19.566702 0l-19.566703-19.566702a13.837838 13.837838 0 0 1 0-19.566703L803.258811 498.162162l-205.491892-205.491892a13.837838 13.837838 0 0 1 0-19.566702l19.566703-19.566703a13.837838 13.837838 0 0 1 19.566702 0l205.491892 205.491892 205.491892-205.491892a13.837838 13.837838 0 0 1 19.566703 0l19.566703 19.566703a13.837838 13.837838 0 0 1 0 19.566702L881.525622 498.162162l205.491892 205.491892a13.837838 13.837838 0 0 1 0 19.566703z"
              fill="#333333"
            ></path>
          </svg>
        </button>
      </div>

      <!-- 第二行：1/x、x²、√x、÷ -->
      <div class="button-row">
        <button class="btn btn-function" @click="reciprocal" title="倒数 (1/x)">
          <svg width="30" height="30" viewBox="0 0 20 20">
            <text
              x="6"
              y="10"
              font-size="7"
              font-family="Arial, sans-serif"
              font-weight="500"
              fill="currentColor"
            >
              1
            </text>
            <line
              x1="8"
              y1="16"
              x2="14"
              y2="6"
              stroke="currentColor"
              stroke-width="0.6"
            />
            <text
              x="12"
              y="16"
              font-size="7"
              font-family="Arial, sans-serif"
              font-style="italic"
              fill="currentColor"
            >
              x
            </text>
          </svg>
        </button>
        <button class="btn btn-function" @click="square" title="平方 (x²)">
          <svg viewBox="0 0 1024 1024" width="20" height="20">
            <path
              d="M634.34666667 990.22222222L398.72 639.39555555 162.66666667 990.22222222h-60.69333334l268.26666667-391.57333333L123.62666667 245.68888889h60.69333333l216.53333333 316.48L612.58666667 245.68888889h60.69333333L430.93333333 598.75555555l264 391.57333334-60.58666666-0.10666667zM750.93333333 372.19555555v-17.6c9.38666667-23.57333333 21.76-45.33333333 37.33333334-65.38666666 15.36-20.16 36.26666667-41.28 62.29333333-63.78666667 30.61333333-26.45333333 51.84-48.53333333 63.46666667-65.92s17.38666667-36.8 17.38666666-58.02666667c0-22.82666667-6.72-40.53333333-19.94666666-53.65333333-13.22666667-12.90666667-31.36-19.41333333-54.4-19.41333333-20.48 0-37.86666667 6.29333333-52.16 18.88-14.29333333 12.48-22.61333333 29.54666667-24.85333334 51.09333333L755.62666667 94.22222222c2.56-26.13333333 13.01333333-47.14666667 31.68-63.04 18.45333333-15.89333333 42.02666667-23.89333333 70.61333333-23.89333333 30.4 0 54.50666667 8 72.32 24.10666666 17.70666667 16.10666667 26.45333333 38.4 26.45333333 66.98666667 0 16.64-2.56 31.68-7.57333333 44.90666667s-13.22666667 26.77333333-24.64 40.74666666c-11.41333333 13.97333333-30.08 32.42666667-55.78666667 55.57333334-21.22666667 18.88-39.36 37.12-54.18666666 54.72s-26.45333333 36.05333333-34.77333334 55.25333333h182.4v22.50666667H750.93333333z"
              fill="#000000"
            ></path>
          </svg>
        </button>
        <button
          class="btn btn-function"
          @click="squareRoot"
          title="平方根 (√x)"
        >
          <svg viewBox="0 0 1445 1024" width="20" height="20">
            <path
              d="M923.768471 421.647059c15.36 16.263529 26.985412 32.647529 34.816 48.971294 5.662118 11.264 14.878118 40.96 27.76847 88.907294l41.441883-62.102588c10.962824-15.058824 24.274824-29.394824 39.996235-43.068235 15.661176-13.673412 29.515294-23.009882 41.381647-28.009412 7.529412-3.132235 15.841882-4.698353 24.937412-4.698353 13.492706 0 24.214588 3.614118 32.225882 10.842353s12.047059 15.962353 12.047059 26.322823c0 11.926588-2.409412 20.118588-7.107765 24.45553a43.971765 43.971765 0 0 1-30.117647 11.806117c-6.625882 0-13.613176-1.445647-21.142588-4.21647-14.757647-5.059765-24.696471-7.589647-29.635765-7.589647-7.529412 0-16.504471 4.397176-26.864941 13.251764-19.456 16.263529-42.646588 47.766588-69.632 94.569412l38.550588 161.852235c6.023529 24.756706 11.023059 39.634824 15.058824 44.453648 4.096 4.879059 8.192 7.288471 12.288 7.28847 6.625882 0 14.275765-3.614118 23.070117-10.842353 17.227294-14.396235 31.924706-33.189647 44.212706-56.44047l16.444236 8.432941a294.671059 294.671059 0 0 1-75.294118 92.28047c-17.227294 13.854118-31.804235 20.720941-43.730824 20.720942-17.588706 0-31.563294-9.878588-41.863529-29.635765-6.625882-12.288-20.239059-64.933647-40.96-158.117647-48.971294 84.992-88.184471 139.745882-117.639529 164.201412-19.154824 15.661176-37.647059 23.491765-55.536942 23.491764a50.838588 50.838588 0 0 1-34.334117-13.613176 35.84 35.84 0 0 1-11.324236-27.708236c0-10.420706 3.433412-19.034353 10.360471-25.901176a34.575059 34.575059 0 0 1 25.419294-10.360471c9.999059 0 20.660706 4.999529 31.984941 15.058824 8.131765 7.228235 14.456471 10.842353 18.853647 10.842353 3.734588 0 8.613647-2.529882 14.576942-7.589647 14.697412-11.866353 34.816-37.647059 60.235294-77.101177 25.419294-39.574588 42.044235-68.126118 49.874823-85.714823a5098.315294 5098.315294 0 0 0-31.563294-120.952471c-7.228235-20.359529-16.624941-34.816-28.190118-43.248941-11.625412-8.432941-28.732235-12.709647-51.32047-12.709647-7.228235 0-15.540706 0.301176-24.937412 0.903529v-16.865882L923.768471 421.647059zM164.201412 523.806118l-90.473412 45.236706a30.117647 30.117647 0 0 1-26.985412-53.850353l120.470588-60.235295a30.117647 30.117647 0 0 1 41.682824 16.323765l143.661176 383.096471L512.602353 54.392471A30.117647 30.117647 0 0 1 542.117647 30.117647h843.294118a30.117647 30.117647 0 1 1 0 60.235294H566.814118L390.927059 969.667765c-5.903059 29.696-47.104 33.069176-57.705412 4.698353l-168.96-450.56z"
              fill="#000000"
            ></path>
          </svg>
        </button>
        <button class="btn btn-operator" @click="inputOperator('/')">
          <svg viewBox="0 0 1461 1024" width="20" height="20">
            <path
              d="M0 464.484686m55.698745 0l1349.623431 0q55.698745 0 55.698745 55.698745l0 0q0 55.698745-55.698745 55.698745l-1349.623431 0q-55.698745 0-55.698745-55.698745l0 0q0-55.698745 55.698745-55.698745Z"
              fill="#333"
            ></path>
            <path
              d="M729.082287 100.686192m-100.686192 0a100.686192 100.686192 0 1 0 201.372385 0 100.686192 100.686192 0 1 0-201.372385 0Z"
              fill="#333"
            ></path>
            <path
              d="M729.082287 923.313808m-100.686192 0a100.686192 100.686192 0 1 0 201.372385 0 100.686192 100.686192 0 1 0-201.372385 0Z"
              p-id="33119"
            ></path>
          </svg>
        </button>
      </div>

      <!-- 第三行：7、8、9、× -->
      <div class="button-row">
        <button class="btn btn-number" @click="inputNumber('7')">7</button>
        <button class="btn btn-number" @click="inputNumber('8')">8</button>
        <button class="btn btn-number" @click="inputNumber('9')">9</button>
        <button class="btn btn-operator" @click="inputOperator('*')">
          <svg viewBox="0 0 1024 1024" width="20" height="20">
            <path
              d="M198.4 243.2c-6.4-12.8-6.4-32 0-44.8 12.8-6.4 32-6.4 44.8 0l582.4 582.4c12.8 12.8 12.8 32 0 44.8-12.8 6.4-32 6.4-44.8 0L198.4 243.2z m0 0"
              fill="#333"
            ></path>
            <path
              d="M780.8 198.4c12.8-6.4 32-6.4 44.8 0 12.8 12.8 12.8 32 0 44.8l-582.4 582.4c-12.8 6.4-32 6.4-44.8 0-6.4-12.8-6.4-32 0-44.8l582.4-582.4z m0 0"
              fill="#333"
            ></path>
          </svg>
        </button>
      </div>

      <!-- 第四行：4、5、6、- -->
      <div class="button-row">
        <button class="btn btn-number" @click="inputNumber('4')">4</button>
        <button class="btn btn-number" @click="inputNumber('5')">5</button>
        <button class="btn btn-number" @click="inputNumber('6')">6</button>
        <button class="btn btn-operator" @click="inputOperator('-')">
          <svg viewBox="0 0 1024 1024" width="20" height="20">
            <path d="M128 512h768v46.933333H128z" fill="#333"></path>
          </svg>
        </button>
      </div>

      <!-- 第五行：1、2、3、+ -->
      <div class="button-row">
        <button class="btn btn-number" @click="inputNumber('1')">1</button>
        <button class="btn btn-number" @click="inputNumber('2')">2</button>
        <button class="btn btn-number" @click="inputNumber('3')">3</button>
        <button class="btn btn-operator" @click="inputOperator('+')">
          <svg viewBox="0 0 1024 1024" width="20" height="20">
            <path
              d="M863.328262 481.340895l-317.344013 0.099772L545.984249 162.816826c0-17.664722-14.336138-32.00086-32.00086-32.00086s-31.99914 14.336138-31.99914 32.00086l0 318.400215-322.368714-0.17718c-0.032684 0-0.063647 0-0.096331 0-17.632039 0-31.935493 14.239806-32.00086 31.904529-0.096331 17.664722 14.208843 32.031824 31.871845 32.095471l322.59234 0.17718 0 319.167424c0 17.695686 14.336138 32.00086 31.99914 32.00086s32.00086-14.303454 32.00086-32.00086L545.982529 545.440667l317.087703-0.099772c0.063647 0 0.096331 0 0.127295 0 17.632039 0 31.935493-14.239806 32.00086-31.904529S880.960301 481.404542 863.328262 481.340895z"
              fill="#333333"
            ></path>
          </svg>
        </button>
      </div>

      <!-- 第六行：±、0、.、= -->
      <div class="button-row">
        <button class="btn btn-function" @click="toggleSign">
          <svg viewBox="0 0 1024 1024" width="25" height="25">
            <path
              d="M552.96 747.52h225.28v48.64h-225.28zM655.36 227.84l38.4 23.04-314.88 545.28-38.4-23.04zM245.76 314.88h225.28v48.64H245.76z"
              fill="#333333"
            ></path>
            <path d="M335.36 227.84H384v225.28h-48.64z" fill="#333333"></path>
          </svg>
        </button>
        <button class="btn btn-number" @click="inputNumber('0')">0</button>
        <button class="btn btn-function" @click="inputDecimal">.</button>
        <button class="btn btn-equals" @click="inputOperator('=')">＝</button>
      </div>
    </div>

    <!-- 抽屉菜单 -->
    <div class="drawer-overlay" v-if="showDrawer" @click="closeDrawer"></div>
    <div class="drawer-menu" :class="{ 'drawer-open': showDrawer }">
      <div class="drawer-header">
        <h3>计算器</h3>
        <button class="btn btn-close" @click="closeDrawer" title="关闭菜单">
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path
              d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
      <div class="drawer-content">
        <div
          class="mode-option"
          :class="{ active: currentMode === 'standard' }"
          @click="switchMode('standard')"
        >
          <div class="mode-icon">
            <svg width="20" height="20" viewBox="0 0 16 16">
              <path
                d="M2 2h12a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm0 1v10h12V3H2z"
                fill="currentColor"
              />
              <path
                d="M4 5h2v2H4V5zm3 0h2v2H7V5zm3 0h2v2h-2V5zM4 8h2v2H4V8zm3 0h2v2H7V8zm3 0h2v2h-2V8z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div class="mode-info">
            <div class="mode-title">标准型</div>
            <div class="mode-desc">基本的算术运算</div>
          </div>
          <div class="mode-check" v-if="currentMode === 'standard'">
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path
                d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
        <div
          class="mode-option"
          :class="{ active: currentMode === 'scientific' }"
          @click="switchMode('scientific')"
        >
          <div class="mode-icon">
            <svg width="20" height="20" viewBox="0 0 16 16">
              <path
                d="M2 2h12a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm0 1v10h12V3H2z"
                fill="currentColor"
              />
              <path
                d="M3 4h1v1H3V4zm2 0h1v1H5V4zm2 0h1v1H7V4zm2 0h1v1H9V4zm2 0h1v1h-1V4zM3 6h1v1H3V6zm2 0h1v1H5V6zm2 0h1v1H7V6zm2 0h1v1H9V6zm2 0h1v1h-1V6zM3 8h1v1H3V8zm2 0h1v1H5V8zm2 0h1v1H7V8zm2 0h1v1H9V8zm2 0h1v1h-1V8zM3 10h1v1H3v-1zm2 0h1v1H5v-1zm2 0h1v1H7v-1zm2 0h1v1H9v-1zm2 0h1v1h-1v-1z"
                fill="currentColor"
              />
            </svg>
          </div>
          <div class="mode-info">
            <div class="mode-title">科学型</div>
            <div class="mode-desc">高级数学函数和运算</div>
          </div>
          <div class="mode-check" v-if="currentMode === 'scientific'">
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path
                d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- 历史记录面板 -->
    <transition name="history-slide">
      <div class="history-overlay" v-if="showHistory" @click="toggleHistory">
        <div class="history-panel" @click.stop>
          <div class="history-header">
            <h3>历史记录</h3>
            <div class="history-actions">
              <button
                class="btn btn-close"
                @click="toggleHistory"
                title="关闭历史记录"
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <path
                    d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="history-list">
            <div
              v-for="(item, index) in history"
              :key="index"
              class="history-item"
            >
              <div class="history-content" @click="useHistoryResult(item)">
                {{ item }}
              </div>
              <button
                class="btn btn-delete"
                @click="removeHistory(index)"
                title="删除此条记录"
              >
                <svg width="14" height="14" viewBox="0 0 16 16">
                  <path
                    d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <div v-if="history.length === 0" class="history-empty">
              暂无历史记录
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 内存记录面板 -->
    <transition name="memory-slide">
      <div
        class="memory-overlay"
        v-if="showMemoryList"
        @click="toggleMemoryList"
      >
        <div class="memory-panel" @click.stop>
          <div class="memory-header">
            <h3>内存记录</h3>
            <div class="memory-actions">
              <button
                class="btn btn-close"
                @click="toggleMemoryList"
                title="关闭内存记录"
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <path
                    d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="memory-list">
            <div
              v-for="(item, index) in memoryList"
              :key="index"
              class="memory-item"
            >
              <div class="memory-value" @click="useMemoryValue(item)">
                {{ item }}
              </div>
              <div class="memory-actions">
                <button
                  class="btn btn-mini"
                  @click="memoryItemClear(index)"
                  title="清除此项"
                >
                  MC
                </button>
                <button
                  class="btn btn-mini"
                  @click="memoryItemAdd(item, index)"
                  title="内存加法"
                >
                  M+
                </button>
                <button
                  class="btn btn-mini"
                  @click="memoryItemSubtract(item, index)"
                  title="内存减法"
                >
                  M-
                </button>
              </div>
            </div>
            <div v-if="memoryList.length === 0" class="memory-empty">
              内存中未保存任何内容
            </div>
          </div>
          <div class="memory-footer" v-if="memoryList.length > 0">
            <button
              class="btn btn-clear-memory"
              @click="clearAllMemory"
              title="清空所有内存记录"
            >
              <svg width="14" height="14" viewBox="0 0 16 16">
                <path
                  d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from "vue";
import { useCalculator } from "../hooks/useCalculator";
import Decimal from "decimal.js";

/**
 * 计算器 Hook
 */
const calculatorStore = useCalculator();

/**
 * 主显示屏DOM引用
 */
const displayRef = ref<HTMLElement>();

/**
 * 显示历史记录面板
 */
const showHistory = ref(false);

/**
 * 显示抽屉菜单
 */
const showDrawer = ref(false);

/**
 * 显示内存记录面板
 */
const showMemoryList = ref(false);

/**
 * 内存记录列表
 */
const memoryList = ref<string[]>([]);

/**
 * 当前计算器模式
 */
const currentMode = ref<"standard" | "scientific">("standard");

/**
 * 当前模式显示文本
 */
const currentModeText = computed(() => {
  return currentMode.value === "standard" ? "标准型" : "科学型";
});

/**
 * 计算属性
 */
const displayText = computed(() => calculatorStore.displayText.value);
const expressionText = computed(() => calculatorStore.expressionText.value);
const history = computed(() => calculatorStore.history.value);

/**
 * 动态字体大小
 */
const dynamicFontSize = ref("32px");

/**
 * 计算动态字体大小
 */
function calculateFontSize(): void {
  nextTick(() => {
    if (!displayRef.value) return;

    const container = displayRef.value;
    const text = displayText.value;
    const containerWidth = container.clientWidth - 32; // 减去padding

    // 基础字体大小
    const baseFontSize = 32;
    const minFontSize = 16;

    // 创建临时元素来测量文字宽度
    const tempElement = document.createElement("span");
    tempElement.style.visibility = "hidden";
    tempElement.style.position = "absolute";
    tempElement.style.fontSize = `${baseFontSize}px`;
    tempElement.style.fontWeight = "300";
    tempElement.style.fontFamily = getComputedStyle(container).fontFamily;
    tempElement.textContent = text;

    document.body.appendChild(tempElement);
    const textWidth = tempElement.offsetWidth;
    document.body.removeChild(tempElement);

    // 计算缩放比例
    let fontSize = baseFontSize;
    if (textWidth > containerWidth) {
      fontSize = Math.max(
        minFontSize,
        (containerWidth / textWidth) * baseFontSize
      );
    }

    dynamicFontSize.value = `${fontSize}px`;
  });
}

/**
 * 监听显示文本变化，自动调整字体大小
 */
watch(displayText, calculateFontSize, { immediate: true });

/**
 * 输入数字
 */
function inputNumber(num: string): void {
  calculatorStore.inputNumber(num);
}

/**
 * 输入小数点
 */
function inputDecimal(): void {
  calculatorStore.inputDecimal();
}

/**
 * 输入操作符
 */
function inputOperator(operator: string): void {
  calculatorStore.inputOperator(operator as any);
}

/**
 * 清除当前输入
 */
function clearEntry(): void {
  calculatorStore.clearEntry();
}

/**
 * 清除所有
 */
function clearAll(): void {
  calculatorStore.clearAll();
}

/**
 * 退格
 */
function backspace(): void {
  calculatorStore.backspace();
}

/**
 * 正负号切换
 */
function toggleSign(): void {
  calculatorStore.toggleSign();
}

/**
 * 百分号
 */
function percentage(): void {
  calculatorStore.percentage();
}

/**
 * 平方根
 */
function squareRoot(): void {
  calculatorStore.squareRoot();
}

/**
 * 平方
 */
function square(): void {
  calculatorStore.square();
}

/**
 * 倒数
 */
function reciprocal(): void {
  calculatorStore.reciprocal();
}

/**
 * 内存存储
 */
function memoryStore(): void {
  const currentValue = displayText.value;
  calculatorStore.memoryStore();
  // 将当前值添加到内存记录列表
  if (currentValue !== "0" && !memoryList.value.includes(currentValue)) {
    memoryList.value.unshift(currentValue);
  }
}

/**
 * 内存读取
 */
function memoryRecall(): void {
  if (memoryList.value.length > 0) {
    calculatorStore.display.value = memoryList.value[0] || "0";
    calculatorStore.waitingForNewValue.value = true;
  }
}

/**
 * 内存清除
 */
function memoryClear(): void {
  calculatorStore.memoryClear();
  memoryList.value = [];
}

/**
 * 内存加法：将当前显示值与内存记录第一条进行加法运算
 */
function memoryAdd(): void {
  try {
    const currentValue = calculatorStore.display.value || "0";
    const firstMemoryValue =
      memoryList.value.length > 0 ? memoryList.value[0] || "0" : "0";

    // 使用 Decimal.js 进行高精度计算
    const current = new Decimal(currentValue);
    const memory = new Decimal(firstMemoryValue);
    console.log(current.plus(memory));

    const result = current.plus(memory).toString();

    if (memoryList.value.length > 0) {
      // 如果有记录，更新第一条
      memoryList.value[0] = result;
    } else {
      // 如果没有记录，添加新记录
      memoryList.value.unshift(result);
    }
  } catch (error) {
    console.error("内存加法计算错误:", error);
    // 降级到原生计算
    const currentValue = parseFloat(calculatorStore.display.value) || 0;
    const firstMemoryValue =
      memoryList.value.length > 0
        ? parseFloat(memoryList.value[0] || "0") || 0
        : 0;
    const result = currentValue + firstMemoryValue;

    if (memoryList.value.length > 0) {
      memoryList.value[0] = result.toString();
    } else {
      memoryList.value.unshift(result.toString());
    }
  }
}

/**
 * 内存减法：将内存记录第一条减去当前显示值
 */
function memorySubtract(): void {
  try {
    const currentValue = calculatorStore.display.value || "0";
    const firstMemoryValue =
      memoryList.value.length > 0 ? memoryList.value[0] || "0" : "0";

    // 使用 Decimal.js 进行高精度计算
    const current = new Decimal(currentValue);
    const memory = new Decimal(firstMemoryValue);
    const result = memory.minus(current).toString();

    if (memoryList.value.length > 0) {
      // 如果有记录，更新第一条
      memoryList.value[0] = result;
    } else {
      // 如果没有记录，添加新记录
      memoryList.value.unshift(result);
    }
  } catch (error) {
    console.error("内存减法计算错误:", error);
    // 降级到原生计算
    const currentValue = parseFloat(calculatorStore.display.value) || 0;
    const firstMemoryValue =
      memoryList.value.length > 0
        ? parseFloat(memoryList.value[0] || "0") || 0
        : 0;
    const result = firstMemoryValue - currentValue;

    if (memoryList.value.length > 0) {
      memoryList.value[0] = result.toString();
    } else {
      memoryList.value.unshift(result.toString());
    }
  }
}

/**
 * 切换历史记录显示
 */
function toggleHistory(): void {
  showHistory.value = !showHistory.value;
}

/**
 * 切换内存记录面板显示
 */
function toggleMemoryList(): void {
  showMemoryList.value = !showMemoryList.value;
}

/**
 * 使用内存值
 */
function useMemoryValue(value: string): void {
  calculatorStore.display.value = value;
  calculatorStore.waitingForNewValue.value = true;
  showMemoryList.value = false;
}

/**
 * 清空所有内存记录
 */
function clearAllMemory(): void {
  memoryList.value = [];
}

/**
 * 清除指定内存记录项
 */
function memoryItemClear(index: number): void {
  memoryList.value.splice(index, 1);
}

/**
 * 内存加法：将当前显示值加到指定内存记录上
 */
function memoryItemAdd(memoryValue: string, index: number): void {
  try {
    const currentValue = calculatorStore.display.value || "0";

    // 使用 Decimal.js 进行高精度计算
    const current = new Decimal(currentValue);
    const memory = new Decimal(memoryValue || "0");
    const result = current.plus(memory).toString();

    memoryList.value[index] = result;
  } catch (error) {
    console.error("内存项加法计算错误:", error);
    // 降级到原生计算
    const currentValue = parseFloat(calculatorStore.display.value) || 0;
    const memValue = parseFloat(memoryValue) || 0;
    const result = currentValue + memValue;
    memoryList.value[index] = result.toString();
  }
}

/**
 * 内存减法：将当前显示值从指定内存记录中减去
 */
function memoryItemSubtract(memoryValue: string, index: number): void {
  try {
    const currentValue = calculatorStore.display.value || "0";

    // 使用 Decimal.js 进行高精度计算
    const current = new Decimal(currentValue);
    const memory = new Decimal(memoryValue || "0");
    const result = memory.minus(current).toString();

    memoryList.value[index] = result;
  } catch (error) {
    console.error("内存项减法计算错误:", error);
    // 降级到原生计算
    const currentValue = parseFloat(calculatorStore.display.value) || 0;
    const memValue = parseFloat(memoryValue) || 0;
    const result = memValue - currentValue;
    memoryList.value[index] = result.toString();
  }
}

/**
 * 切换抽屉菜单显示
 */
function toggleDrawer(): void {
  showDrawer.value = !showDrawer.value;
}

/**
 * 关闭抽屉菜单
 */
function closeDrawer(): void {
  showDrawer.value = false;
}

/**
 * 切换计算器模式
 */
function switchMode(mode: "standard" | "scientific"): void {
  currentMode.value = mode;
  closeDrawer();
  // 切换模式时清除当前计算
  calculatorStore.clearAll();
}

/**
 * 科学计算器函数
 */
function scientificFunction(func: string): void {
  calculatorStore.scientificFunction(func);
}

/**
 * 阶乘函数
 */
function factorial(): void {
  calculatorStore.factorial();
}

/**
 * 删除单条历史记录
 */
function removeHistory(index: number): void {
  calculatorStore.removeHistoryItem(index);
}

/**
 * 使用历史记录结果
 */
function useHistoryResult(historyItem: string): void {
  const result = historyItem.split(" = ")[1];
  if (result) {
    calculatorStore.display.value = result;
    calculatorStore.waitingForNewValue.value = true;
    showHistory.value = false;
  }
}

/**
 * 键盘事件处理
 */
function handleKeydown(event: KeyboardEvent): void {
  event.preventDefault();

  const key = event.key;

  // 数字键
  if (/^[0-9]$/.test(key)) {
    inputNumber(key);
  }
  // 操作符
  else if (key === "+") {
    inputOperator("+");
  } else if (key === "-") {
    inputOperator("-");
  } else if (key === "*") {
    inputOperator("*");
  } else if (key === "/") {
    inputOperator("/");
  } else if (key === "Enter" || key === "=") {
    inputOperator("=");
  }
  // 小数点
  else if (key === ".") {
    inputDecimal();
  }
  // 退格
  else if (key === "Backspace") {
    backspace();
  }
  // 清除
  else if (key === "Escape") {
    clearAll();
  }
  // Delete键清除当前输入
  else if (key === "Delete") {
    clearEntry();
  }
}

/**
 * 组件挂载时添加键盘监听
 */

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.calculator {
  width: 100%;
  height: 100%;
  max-height: 100vh;
  background-color: var(--primary-bg);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-medium);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
}

/* 工具栏区域 */
.toolbar-area {
  background-color: var(--display-bg);
  border-bottom: 1px solid var(--border-light);
  padding: var(--spacing-sm) var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  flex-shrink: 0;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.toolbar-right {
  display: flex;
  align-items: center;
}

.mode-display {
  font-size: var(--font-size-small);
  color: var(--text-secondary);
  font-weight: 500;
}

.btn-menu,
.toolbar-area .btn-memory {
  background-color: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-xs);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.btn-menu:hover,
.toolbar-area .btn-memory:hover {
  background-color: var(--button-hover);
}

/* 显示区域 */
.display-area {
  background-color: var(--display-bg);
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-light);
  min-height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: right;
  gap: var(--spacing-xs);
  flex-shrink: 0;
}

.expression {
  font-size: var(--font-size-small);
  color: var(--text-secondary);
  min-height: 20px;
  word-break: break-all;
  display: flex;
  align-items: flex-end;
  opacity: 1;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
}

.expression:empty {
  opacity: 0;
}

.display {
  font-weight: 300;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  min-height: 40px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  transition: font-size 0.2s ease;
}

/* 内存按钮区域 */
.memory-section {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  background-color: var(--border-light);
  flex-shrink: 0;
}

.btn-memory {
  height: 36px;
  font-size: var(--font-size-small);
  background-color: var(--secondary-bg);
  border: none;
  border-radius: 0;
}

.btn-memory:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-memory:not(:disabled):hover {
  background-color: var(--button-hover);
}

/* 按钮区域 */
.buttons-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background-color: var(--secondary-bg);
  padding: 4px;
}

.button-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  flex: 1;
  min-height: 0;
}

/* 科学型模式下按钮行高度调整 */
.calculator:has(.scientific-row) .button-row {
  flex: 0.8;
}

.calculator:has(.scientific-row) .scientific-row {
  flex: 0.6;
}

.button-row .btn {
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-button);
  font-weight: 400;
  height: 100%;
  min-height: 40px;
}

/* 科学型模式下按钮样式调整 */
.calculator:has(.scientific-row) .button-row .btn {
  min-height: 35px;
  font-size: var(--font-size-small);
}

.calculator:has(.scientific-row) .scientific-row .btn {
  min-height: 30px;
  font-size: 12px;
}

/* 数字按钮 */
.btn-number {
  background-color: var(--button-bg);
  font-weight: 500;
}

.btn-number:hover {
  background-color: var(--button-hover);
}

/* 功能按钮 */
.btn-function {
  background-color: var(--button-bg);
  font-size: var(--font-size-small);
}

.btn-function:hover {
  background-color: var(--operator-hover);
}

/* 操作符按钮 */
.btn-operator {
  background-color: var(--button-bg);
  font-size: 18px;
  font-weight: 500;
}

.btn-operator:hover {
  background-color: var(--operator-hover);
}

/* 等号按钮 */
.btn-equals {
  background-color: var(--equals-bg);
  color: var(--text-white);
  font-size: 18px;
  font-weight: 600;
}

.btn-equals:hover {
  background-color: var(--equals-hover);
}

/* 清除按钮 */
.btn-clear {
  background-color: var(--clear-bg);
}

.btn-clear:hover {
  background-color: var(--clear-hover);
}

/* 抽屉菜单 */
.drawer-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 15;
  transition: opacity 0.3s ease;
}

.drawer-menu {
  position: absolute;
  top: 0;
  left: -280px;
  width: 280px;
  height: 100%;
  background-color: var(--display-bg);
  border-right: 1px solid var(--border-light);
  box-shadow: var(--shadow-medium);
  display: flex;
  flex-direction: column;
  z-index: 20;
  transition: left 0.3s ease;
}

.drawer-menu.drawer-open {
  left: 0;
}

.drawer-header {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--border-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 44px;
}

.drawer-header h3 {
  margin: 0;
  font-size: var(--font-size-button);
  font-weight: 600;
  color: var(--text-primary);
}

.drawer-content {
  flex: 1;
  padding: var(--spacing-md);
  overflow-y: auto;
}

.mode-option {
  display: flex;
  align-items: center;
  padding: var(--spacing-md);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: var(--spacing-sm);
  gap: var(--spacing-md);
  border: 1px solid transparent;
}

.mode-option:hover {
  background-color: var(--button-hover);
}

.mode-option.active {
  background-color: var(--button-active);
  border-color: var(--equals-bg);
}

.mode-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: var(--secondary-bg);
  border-radius: var(--border-radius-md);
  color: var(--text-primary);
}

.mode-option.active .mode-icon {
  background-color: var(--equals-bg);
  color: var(--text-white);
}

.mode-info {
  flex: 1;
}

.mode-title {
  font-size: var(--font-size-button);
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.mode-desc {
  font-size: var(--font-size-small);
  color: var(--text-secondary);
}

.mode-check {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--equals-bg);
}

/* 历史记录遮罩层 */
.history-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;
  display: flex;
  align-items: flex-end;
}

/* 历史记录面板 */
.history-panel {
  width: 100%;
  height: calc(
    100% - 188px
  ); /* 计算器总高度减去工具栏、显示区域、内存区域的高度 */
  background-color: var(--display-bg);
  border-top: 1px solid var(--border-light);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
}

/* 历史记录面板过渡动画 */
.history-slide-enter-active {
  transition: all 0.3s ease;
}

.history-slide-leave-active {
  transition: all 0.3s ease;
}

.history-slide-enter-from {
  opacity: 0;
}

.history-slide-enter-from .history-panel {
  transform: translateY(100%);
}

.history-slide-enter-to {
  opacity: 1;
}

.history-slide-enter-to .history-panel {
  transform: translateY(0);
}

.history-slide-leave-from {
  opacity: 1;
}

.history-slide-leave-from .history-panel {
  transform: translateY(0);
}

.history-slide-leave-to {
  opacity: 0;
}

.history-slide-leave-to .history-panel {
  transform: translateY(100%);
}

.history-header {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--border-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.history-header h3 {
  margin: 0;
  font-size: var(--font-size-button);
  font-weight: 600;
  color: var(--text-primary);
}

.history-actions {
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
}

.history-actions .btn {
  border: none;
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-xs);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-close {
  background-color: var(--clear-bg);
}

.btn-close:hover {
  background-color: var(--clear-hover);
}

.history-actions .btn-clear {
  background-color: var(--clear-bg);
}

.history-actions .btn-clear:hover {
  background-color: var(--clear-hover);
}

.history-list {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-sm);
}

.history-item {
  display: flex;
  align-items: center;
  padding: var(--spacing-md);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-small);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xs);
  gap: var(--spacing-sm);
}

.history-item:hover {
  background-color: var(--button-hover);
}

.history-content {
  flex: 1;
  cursor: pointer;
  word-break: break-all;
  padding: var(--spacing-xs) 0;
}

.btn-delete {
  background-color: transparent;
  border: none;
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-xs);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--text-secondary);
  opacity: 0.6;
  transition: all 0.2s ease;
}

.btn-delete:hover {
  background-color: var(--clear-bg);
  color: var(--text-primary);
  opacity: 1;
}

.history-empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--font-size-small);
  padding: var(--spacing-xl);
}

/* 内存记录遮罩层 */
.memory-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;
  display: flex;
  align-items: flex-end;
}

/* 内存记录面板 */
.memory-panel {
  width: 100%;
  height: calc(
    100% - 188px
  ); /* 计算器总高度减去工具栏、显示区域、内存区域的高度 */
  background-color: var(--display-bg);
  border-top: 1px solid var(--border-light);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
}

/* 内存记录面板过渡动画 */
.memory-slide-enter-active {
  transition: all 0.3s ease;
}

.memory-slide-leave-active {
  transition: all 0.3s ease;
}

.memory-slide-enter-from {
  opacity: 0;
}

.memory-slide-enter-from .memory-panel {
  transform: translateY(100%);
}

.memory-slide-enter-to {
  opacity: 1;
}

.memory-slide-enter-to .memory-panel {
  transform: translateY(0);
}

.memory-slide-leave-from {
  opacity: 1;
}

.memory-slide-leave-from .memory-panel {
  transform: translateY(0);
}

.memory-slide-leave-to {
  opacity: 0;
}

.memory-slide-leave-to .memory-panel {
  transform: translateY(100%);
}

.memory-header {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--border-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.memory-header h3 {
  margin: 0;
  font-size: var(--font-size-button);
  font-weight: 600;
  color: var(--text-primary);
}

.memory-actions {
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
}

.memory-actions .btn {
  border: none;
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-xs);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 20px;
}

.memory-list {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-xs);
}

.memory-item {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-small);
  color: var(--text-secondary);
  margin-bottom: 2px;
  transition: all 0.2s ease;
  position: relative;
}

.memory-item:hover {
  background-color: var(--button-hover);
}

.memory-value {
  cursor: pointer;
  word-break: break-all;
  padding: 2px 0;
  font-weight: 500;
  color: var(--text-primary);
}

.memory-actions {
  display: flex;
  gap: var(--spacing-xs);
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-top: 2px;
}

.memory-item:hover .memory-actions {
  opacity: 1;
}

.btn-mini {
  background-color: var(--button-bg);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-sm);
  padding: 2px 6px;
  font-size: 10px;
  cursor: pointer;
  color: var(--text-primary);
  transition: all 0.2s ease;
  min-width: 24px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-mini:hover {
  background-color: var(--clear-bg);
  border-color: var(--border-medium);
  transform: scale(1.05);
}

.memory-empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--font-size-small);
  padding: var(--spacing-xl);
}

.memory-footer {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-top: 1px solid var(--border-light);
  display: flex;
  justify-content: flex-end;
}

.btn-clear-memory {
  background-color: var(--clear-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-small);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-clear-memory:hover {
  background-color: var(--clear-hover);
}

/* SVG图标样式 */
svg {
  display: block;
}

/* 响应式调整 */
@media (max-width: 320px) {
  .calculator {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
}
</style>
